export type ClientSessionMetadata = Record<string, unknown>

const isIntlDisplayNamesAvailable = (): boolean => {
  try {
    return (
      typeof Intl !== 'undefined' &&
      typeof (Intl as any).DisplayNames === 'function'
    )
  } catch {
    return false
  }
}

const normalizeLocale = (locale?: string | null): string | undefined => {
  if (!locale) return undefined
  return locale.replace('_', '-')
}

const deriveCountryFromLocale = (
  locale?: string | null
): string | undefined => {
  const normalized = normalizeLocale(locale)
  if (!normalized) return undefined
  const parts = normalized.split('-')
  if (parts.length >= 2) {
    const region = parts[1]
    if (region && /^[A-Za-z]{2,3}$/.test(region)) {
      return region.toUpperCase()
    }
  }

  try {
    const intlLocale = new Intl.Locale(normalized)
    if (intlLocale && intlLocale.maximize) {
      const maximized = intlLocale.maximize()
      if (maximized?.region) {
        return maximized.region.toUpperCase()
      }
    }
  } catch {
    // Ignore errors from Intl.Locale on unsupported environments
  }

  return undefined
}

const deriveCountryName = (
  countryCode?: string,
  language?: string
): string | undefined => {
  if (!countryCode) return undefined
  if (!isIntlDisplayNamesAvailable()) {
    return countryCode
  }

  try {
    const display = new Intl.DisplayNames(language ? [language] : undefined, {
      type: 'region',
    })
    return display.of(countryCode) ?? countryCode
  } catch {
    return countryCode
  }
}

const inferOsFromPlatform = (platform?: string): string | undefined => {
  if (!platform) return undefined
  const normalized = platform.toLowerCase()
  if (normalized.includes('mac')) return 'macOS'
  if (normalized.includes('win')) return 'Windows'
  if (normalized.includes('linux')) return 'Linux'
  if (normalized.includes('iphone') || normalized.includes('ipad')) {
    return 'iOS'
  }
  if (normalized.includes('android')) return 'Android'
  return undefined
}

const inferBrowserFromUserAgent = (userAgent?: string): string | undefined => {
  if (!userAgent) return undefined
  const normalized = userAgent.toLowerCase()
  if (normalized.includes('edg/')) return 'Microsoft Edge'
  if (normalized.includes('opr/') || normalized.includes('opera'))
    return 'Opera'
  if (
    normalized.includes('chrome') &&
    !normalized.includes('chromium') &&
    !normalized.includes('edg/') &&
    !normalized.includes('opr/') &&
    !normalized.includes('opera')
  ) {
    return 'Chrome'
  }
  if (
    normalized.includes('safari') &&
    !normalized.includes('chrome') &&
    !normalized.includes('crios')
  ) {
    return 'Safari'
  }
  if (normalized.includes('firefox')) return 'Firefox'
  if (normalized.includes('msie') || normalized.includes('trident')) {
    return 'Internet Explorer'
  }
  return undefined
}

const inferOsFromUserAgent = (userAgent?: string): string | undefined => {
  if (!userAgent) return undefined
  const normalized = userAgent.toLowerCase()
  if (normalized.includes('android')) return 'Android'
  if (
    normalized.includes('iphone') ||
    normalized.includes('ipad') ||
    normalized.includes('ipod') ||
    normalized.includes('ios')
  ) {
    return 'iOS'
  }
  if (normalized.includes('windows nt')) return 'Windows'
  if (
    normalized.includes('mac os x') ||
    normalized.includes('macintosh') ||
    normalized.includes('macintel')
  ) {
    return 'macOS'
  }
  if (normalized.includes('cros')) return 'ChromeOS'
  if (normalized.includes('linux')) return 'Linux'
  return undefined
}

const deriveTimezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

export const collectClientSessionMetadata = (): ClientSessionMetadata => {
  if (typeof window === 'undefined') {
    return {}
  }

  const metadata: Record<string, unknown> = {}
  const nav = window.navigator

  const userAgent = nav.userAgent
  const language = nav.language
  const languages = Array.isArray(nav.languages)
    ? nav.languages.filter(item => typeof item === 'string' && item.length > 0)
    : undefined
  const platform = nav.platform
  const timezone = deriveTimezone()
  const locale = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().locale
    } catch {
      return undefined
    }
  })()

  const countryFromLocale =
    deriveCountryFromLocale(locale) ?? deriveCountryFromLocale(language)

  metadata.userAgent = userAgent
  metadata.language = language
  if (languages && languages.length > 0) {
    metadata.languages = languages
  }
  metadata.locale = locale
  metadata.timezone = timezone
  metadata.platform = platform
  metadata.hardwareConcurrency = nav.hardwareConcurrency
  metadata.deviceMemory = (nav as any)?.deviceMemory
  metadata.doNotTrack = nav.doNotTrack

  const inferredOs =
    inferOsFromUserAgent(userAgent) ?? inferOsFromPlatform(platform)
  if (inferredOs) {
    metadata.os = inferredOs
  }

  const inferredBrowser = inferBrowserFromUserAgent(userAgent)
  if (inferredBrowser) {
    metadata.browser = inferredBrowser
  }

  const countryCode = countryFromLocale
  if (countryCode) {
    metadata.country = countryCode
    metadata.countryName = deriveCountryName(countryCode, language)
  }

  metadata.currentUrl = window.location?.href
  metadata.pathname = window.location?.pathname
  metadata.referrer = document?.referrer

  metadata.screen = {
    width: window.screen?.width,
    height: window.screen?.height,
    availWidth: window.screen?.availWidth,
    availHeight: window.screen?.availHeight,
    pixelRatio: window.devicePixelRatio,
  }

  const connection = (nav as any)?.connection
  if (connection && typeof connection === 'object') {
    const normalizedConnection: Record<string, unknown> = {}
    if (connection.downlink) normalizedConnection.downlink = connection.downlink
    if (connection.effectiveType)
      normalizedConnection.effectiveType = connection.effectiveType
    if (connection.rtt) normalizedConnection.rtt = connection.rtt
    if (connection.saveData !== undefined)
      normalizedConnection.saveData = connection.saveData
    if (connection.type) normalizedConnection.type = connection.type
    if (Object.keys(normalizedConnection).length > 0) {
      metadata.connection = normalizedConnection
    }
  }

  return Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        (typeof value !== 'string' || value.trim().length > 0)
    )
  )
}
